import React from 'react';
import { Alert, Box, Typography } from '@mui/material';
import { useStringsContext } from '../../../../contexts/strings';
import { useStoryNotificationConf } from '../../../../hooks/useStories';
import { useSegments } from '../../../../hooks/useSegments';
import moment from 'moment';
import { StoryDataGridFormat } from '../data';
import PushNotificationPopup, {
  PushNotificationFormPayload,
} from '../../../common/push-notification/push-notification-form';
import PushNotificationColumn from '../../../common/push-notification/push-notification-column';

interface Props {
  story: StoryDataGridFormat;
  selectedAbbrv: string;
}

export default function StoryPushNotificationColumn(props: Props) {
  const { strings } = useStringsContext();
  const { selectedAbbrv, story } = props;
  const [openPushNotificationPopup, setOpenPushNotificationPopup] = React.useState(false);
  const { query, createMutation, updateMutation, deleteMutation } = useStoryNotificationConf({
    abbrv: selectedAbbrv,
    id: story.id,
  });
  const { query: segmentQuery } = useSegments(selectedAbbrv);
  const isLoading = query.isLoading || segmentQuery.isLoading;
  const storyNotificationConf = query.data;
  const pushNotificationId = storyNotificationConf?.id;

  if (isLoading) {
    return <>{strings.pushNotifications.loading}</>;
  }

  return (
    <Box sx={{ display: 'flex', gap: 2 }}>
      <PushNotificationColumn
        pushNotification={storyNotificationConf}
        onClick={() => {
          setOpenPushNotificationPopup(true);
        }}
        abbrv={selectedAbbrv}
      />

      {openPushNotificationPopup ? (
        <PushNotificationPopup
          abbrv={selectedAbbrv}
          segments={segmentQuery.data || []}
          pushNotificationIsSend={storyNotificationConf?.status === 'SENT'}
          itemInfo={
            <Alert icon={false} severity="info" color="info" variant="standard">
              <Typography variant="h6">
                {story.caption} -{' '}
                {story.type === 'VIDEO' ? strings.stories.typeVideo : strings.stories.typeImage}
              </Typography>
              {moment(story.story.startTime).format('YYYY-MM-DD HH:mm')}
            </Alert>
          }
          content={{
            target: storyNotificationConf?.notification?.target,
            scheduledTime: storyNotificationConf?.scheduledTime
              ? moment(storyNotificationConf.scheduledTime).format('YYYY-MM-DD[T]HH:mm')
              : moment(story.story.startTime).isSameOrAfter(moment().add(1, 'hour'))
                ? moment(story.story.startTime).format('YYYY-MM-DD[T]HH:mm')
                : undefined,

            title: storyNotificationConf?.notification?.title || story.caption || '',
            description:
              storyNotificationConf?.notification?.description ||
              (story.type === 'VIDEO' ? strings.stories.typeVideo : strings.stories.typeImage) ||
              '',
          }}
          onCreate={async (payload: PushNotificationFormPayload) => {
            const { scheduledTime, target, title, description } = payload;
            const res = await createMutation.mutateAsync({
              scheduledTime,
              notification: {
                title,
                description,
                image: story.thumbnailUrl,
                storiesId: story.id,
                target,
              },
            });
            query.refetch();
            return res;
          }}
          onUpdate={(payload: PushNotificationFormPayload) => {
            if (!pushNotificationId) {
              return Promise.resolve();
            }
            const { scheduledTime, target, title, description } = payload;
            return updateMutation.mutateAsync({
              id: pushNotificationId,
              scheduledTime,
              notification: {
                title,
                description,
                image: story.thumbnailUrl,
                storiesId: story.id,
                target,
              },
              createdAt: (storyNotificationConf?.createdAt || moment().format()) as string,
            });
          }}
          onDelete={async () => {
            if (!pushNotificationId) {
              return Promise.resolve();
            }
            const res = await deleteMutation.mutateAsync({
              id: pushNotificationId,
              storyId: story.id,
            });
            query.refetch();
            return res;
          }}
          onClose={() => {
            setOpenPushNotificationPopup(false);
          }}
          isPending={
            createMutation.isPending ||
            updateMutation.isPending ||
            deleteMutation.isPending ||
            query.isLoading
          }
          isNew={!pushNotificationId}
        />
      ) : null}
    </Box>
  );
}
